import {
  CornerstoneClient,
  generateCodeVerifier,
  generateCodeChallenge,
} from '@mb/platform-sdk';
import { ApiClient } from './api-client';

/**
 * Cornerstone SSO — OAuth 2.0 + PKCE (PLATFORM_INTERFACE.md §2).
 *
 * The browser never sees a client secret: PKCE proves possession of the code
 * verifier instead. The verifier lives in sessionStorage for exactly as long as
 * the round trip to Cornerstone takes, and is deleted the moment it is redeemed.
 */

const CLIENT_ID = import.meta.env.VITE_CORNERSTONE_CLIENT_ID ?? '';
const API_URL = import.meta.env.VITE_CORNERSTONE_API_URL ?? '';
// Must match this app's service `slug` in Cornerstone EXACTLY — not its display
// name. The SDK sends it on service-scoped calls; a mismatch yields 404s that
// look like missing data rather than a misconfiguration.
const SERVICE_SLUG = import.meta.env.VITE_CORNERSTONE_SERVICE_SLUG ?? '';
const REDIRECT_URI =
  import.meta.env.VITE_CORNERSTONE_REDIRECT_URI ??
  (typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : '');

const VERIFIER_KEY = 'cs_pkce_verifier';
const STATE_KEY = 'cs_oauth_state';

/**
 * Cornerstone SSO is offered only when an OAuth client has actually been
 * registered for this deployment. With no client id there is no sign-in button
 * — the capability is absent rather than present-and-broken.
 */
export function isCornerstoneEnabled(): boolean {
  return CLIENT_ID.length > 0 && API_URL.length > 0 && SERVICE_SLUG.length > 0;
}

let client: CornerstoneClient | null = null;
function getClient(): CornerstoneClient {
  if (!client) {
    client = new CornerstoneClient({ baseUrl: API_URL, serviceSlug: SERVICE_SLUG });
  }
  return client;
}

/**
 * Step 1-2: mint a PKCE pair, stash the verifier, and send the browser to
 * Cornerstone. `prompt` is passed through so the post-logout link can force a
 * credential prompt even though `.ministry.builders` cookies are shared.
 */
export async function beginCornerstoneLogin(opts?: { prompt?: 'login' }): Promise<void> {
  if (!isCornerstoneEnabled()) {
    throw new Error('Cornerstone SSO is not configured for this deployment.');
  }
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = crypto.randomUUID();

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  window.location.href = getClient().auth.buildAuthorizeUrl({
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
    ...(opts?.prompt ? { prompt: opts.prompt } : {}),
  });
}

/**
 * Step 3-5: redeem the authorization code at the callback route.
 *
 * `state` is compared before the code is sent anywhere. Without that check an
 * attacker can hand a victim's browser their own authorization code and have the
 * victim's session silently bound to the attacker's identity (login CSRF).
 */
export async function completeCornerstoneLogin(search: string): Promise<void> {
  const params = new URLSearchParams(search);

  const error = params.get('error');
  if (error) {
    throw new Error(params.get('error_description') || `Cornerstone returned "${error}".`);
  }

  const code = params.get('code');
  const returnedState = params.get('state');
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);

  // Single-use: clear before any await so a replayed callback cannot reuse them.
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  if (!code) throw new Error('Cornerstone did not return an authorization code.');
  if (!verifier) throw new Error('This sign-in attempt has expired. Please try again.');
  if (!expectedState || returnedState !== expectedState) {
    throw new Error('Sign-in state did not match. Please start again.');
  }

  const tokens = await getClient().auth.exchangeCode({
    grant_type: 'authorization_code',
    code,
    redirect_uri: REDIRECT_URI,
    client_id: CLIENT_ID,
    code_verifier: verifier,
  });

  // The API accepts the Cornerstone access token directly: middleware/auth.ts
  // dispatches on the token's `alg`, so an RS256 Cornerstone token is verified
  // against Cornerstone's JWKS and an HS256 local token against JWT_SECRET.
  ApiClient.setToken(tokens.access_token);
}

/**
 * Total session destruction (PLATFORM_INTERFACE.md §2 "SSO Logout").
 *
 * Order matters and all three steps are mandatory:
 *   1. call Cornerstone so the shared `.ministry.builders` cookies are cleared
 *      and the token is blacklisted — local-only cleanup leaves the user
 *      silently re-authenticated on the next visit;
 *   2. drop local state;
 *   3. hard navigation, NOT react-router, so in-memory stores and caches die.
 */
export async function cornerstoneLogout(loginUrl = '/sign-in'): Promise<void> {
  try {
    await getClient().auth.logout();
  } catch {
    // Best-effort — never let a failed call strand the user in a logged-in UI.
  }
  ApiClient.clearToken();
  sessionStorage.clear();
  window.location.href = loginUrl;
}
